import { DATATABLES_FIXED_CELL_SELECTOR } from 'ui/datatables/datatables';

import { throttle } from 'cadenza/utils/throttle';
import { AFTER_TABLE_CONFIGURATION, DATATABLE_INITIALIZED } from 'cadenza/ui/base-table/base-table-event-types';
import { getColumnIndexForBackend, getColumnIndexForFrontend } from 'cadenza/ui/base-table/base-table';
import type { TableColumnDto } from 'cadenza/ui/base-table/model/table-column-dto';
import { OlapPivotTablePlugin } from 'cadenza/result-table/olap-pivot-table-plugin';
import type { ResultTable } from 'cadenza/result-table/result-table';
import type { FrozenColumnCount, TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import { FREEZE_ALL_DIMENSION_COLUMNS_COUNT_VALUE } from 'cadenza/result-table/table-configuration-dto';
import { on } from 'cadenza/utils/event-util';
import type { Teardown } from 'cadenza/utils/teardown';
import { tearDown } from 'cadenza/utils/teardown';
import { removeColumnScrollShadow, setupColumnScrollShadow } from 'cadenza/utils/scroll-shadow/scroll-shadow';
import { COLUMN_RESIZED_EVENT_TYPE } from 'cadenza/ui/base-table/resize-column-base-table-plugin';

const COMPONENT_NAME = 'd-frozen-columns-olap-pivot-table-plugin';

const DEFAULT_MIN_SPACE_LEFT = 100;

export class FrozenColumnsOlapPivotTablePlugin extends OlapPivotTablePlugin {

  /**
   * Number of frozen columns that should be applied from user's configuration before respecting
   * the available space on the screen. Measured in Datatable space (incl. index and invisible columns).
   */
  #configuredFrozenColumnCount: number = 0;

  /**
   * Number of frozen columns that have been applied to the table after respecting available space
   * on the screen. Measured in Datatable space (incl. index and invisible columns).
   */
  #effectiveFrozenColumnCount = 0;

  #tearDowns: Teardown[] = [];

  #throttleRecalculationOfEffectiveFrozenColumns = throttle(
    () => this.#recalculateAndApplyFrozenColumSetting(),
    500);

  override configureWithConfigurationData (tableConfiguration: TableConfigurationDto): void {
    if (this.table.isRenderForPrint()) {
      return;
    }

    if (tableConfiguration.frozenColumnSetting || this.table.hasPivotConfiguration()) {
      this.#configuredFrozenColumnCount = this.#calculateFrozenColumnCount(tableConfiguration);
      this.#effectiveFrozenColumnCount = this.#calculateEffectiveFrozenColumnCount(); // initially freeze like planned
    }
  }

  #calculateFrozenColumnCount ({ columns, frozenColumnSetting }: TableConfigurationDto) {
    const frozenColumnCountSettingValue = Number(frozenColumnSetting) as FrozenColumnCount ?? 0;
    const isPivotTable = this.table.hasPivotConfiguration();

    const frozenColumnCount = isPivotTable || this.#isFreezeAllDimensionsColumns(frozenColumnCountSettingValue)
      ? this.#calculateColumnCountForFreezingAllDimensions(columns)
      : frozenColumnCountSettingValue;

    // If all columns should be frozen, freeze none instead
    const visibleColumnCount = columns.reduce(
      (sum, c) => c.visible ? sum + 1 : sum,
      0);
    if (frozenColumnCount === visibleColumnCount) {
      return 0;
    }

    return getColumnIndexForFrontend(frozenColumnCount); // Compensate for index column
  }

  #isFreezeAllDimensionsColumns (frozenColumnSettingCount: FrozenColumnCount) {
    return frozenColumnSettingCount === FREEZE_ALL_DIMENSION_COLUMNS_COUNT_VALUE;
  }

  #calculateColumnCountForFreezingAllDimensions (columns: TableColumnDto[]) {
    const firstMeasureIndex = this.#calculateFirstMeasureIndex(); // In column definition space without index column

    if (firstMeasureIndex === -1) { // Do we have measures? (Only possible in non-pivot tables)
      return 0; // We do not have measures, all are dimensions => No frozen columns
    }

    return findVisibleColumnCountUpUntilTheGivenIndex(firstMeasureIndex, columns);
  }

  #calculateFirstMeasureIndex () {
    const measureColumnIndexes = this.table.getMeasureColumnIndexes();
    return measureColumnIndexes.length ? getColumnIndexForBackend(measureColumnIndexes[0]) : -1;
  }

  override init (): void {
    this.#tearDown();
    this.#tearDowns.push(
      on(this.table, COLUMN_RESIZED_EVENT_TYPE, this.#throttleRecalculationOfEffectiveFrozenColumns));

    // Set up resize observer to react on changed table sizes
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(this.#throttleRecalculationOfEffectiveFrozenColumns);
    });
    resizeObserver.observe(this.table);
    this.#tearDowns.push(resizeObserver);

    // Adapt the datatables configuration on every update of it
    this.table.addEventListener(AFTER_TABLE_CONFIGURATION, (event) => {
      (event as CustomEvent).detail.dataTableOptions.fixedColumns = this.#createDatatablesFixedColumnsConfig();
    });

    // Init scroll shadow when datatables has finished its rendering
    this.table.addEventListener(DATATABLE_INITIALIZED, (event) => {
      (event as CustomEvent).detail.dataTable.on('draw.dt', () => {
        // Wait for next event round so all plugins (including DT fixed columns plugin) has been handled
        setTimeout(this.#throttleRecalculationOfEffectiveFrozenColumns);
      });
      (event as CustomEvent).detail.dataTable.one('draw.dt', () => {
        // DT Fixed columns plugin does not have any event that is triggered when it finished its
        // initialization, anymore. We solve this by hooking on the dt.draw callback which the fixed
        // columns plugin also uses for init.
        // Wait for next event round so all plugins (including DT fixed columns plugin) has been handled
        setTimeout(() => this.#initScrollShadow());
      });
    });
  }

  disconnectedCallback (): void {
    this.#tearDown();
  }

  #tearDown () {
    this.#tearDowns = tearDown(this.#tearDowns);
  }

  #initScrollShadow () {
    if (this.table.isRenderForPrint()) {
      return;
    }

    const fixedBodyScrollBody = this.table.getScrollBody()!;
    const lastFixedColumnCells = [ ...this.table.querySelectorAll<HTMLElement>(DATATABLES_FIXED_CELL_SELECTOR) ]
      .filter(fixedCell => !fixedCell.nextElementSibling?.matches(DATATABLES_FIXED_CELL_SELECTOR));
    if (lastFixedColumnCells.length) {
      setupColumnScrollShadow(fixedBodyScrollBody, ...lastFixedColumnCells);
    }
  }

  #createDatatablesFixedColumnsConfig (): { leftColumns: number } | boolean {
    return this.#effectiveFrozenColumnCount
      ? { leftColumns: this.#effectiveFrozenColumnCount }
      : false;
  }

  #calculateEffectiveFrozenColumnCount () {
    const configuredFrozenColumnCount = this.#configuredFrozenColumnCount;

    if (configuredFrozenColumnCount !== 0
      && this.table.getDataTable()
      && !frozenColumnsFitIntoView(this.table, configuredFrozenColumnCount)) {
      return 0;
    }

    return configuredFrozenColumnCount;
  }

  #recalculateAndApplyFrozenColumSetting () {
    if (!this.table.getDataTable()) {
      return; // Table not yet initialized
    }

    const prevEffectiveFrozenColumnCount = this.#effectiveFrozenColumnCount;
    const newEffectiveFrozenColumnCount = this.#calculateEffectiveFrozenColumnCount();
    if (prevEffectiveFrozenColumnCount !== newEffectiveFrozenColumnCount) {
      this.#effectiveFrozenColumnCount = newEffectiveFrozenColumnCount;
      this.#updateFrozenTableSettingOnTable();
    }
  }

  #updateFrozenTableSettingOnTable () {
    const columnCount = this.#effectiveFrozenColumnCount;
    const dataTable = this.table.getDataTable();
    if (dataTable) {
      dataTable.fixedColumns().left(
        columnCount ? getColumnIndexForFrontend(columnCount) : 0);

      // Toggle scroll shadow
      if (!columnCount) {
        removeColumnScrollShadow(this.table);
      } else {
        this.#initScrollShadow();
      }
    }
  }

}

// returns information if the current table width allows applying frozen column feature (if there is enough space)
function frozenColumnsFitIntoView (table: ResultTable, columnCount: number) {
  const allTableColumnHeaderCells = [ ...table.querySelectorAll<HTMLElement>('thead tr:last-child th') ];
  const tableColumnsCount = allTableColumnHeaderCells.length;
  const minScrollableSpace = calculateMinScrollableSpace(
    tableColumnsCount,
    columnCount,
    allTableColumnHeaderCells,
    table);

  const dataTable = table.getDataTable()!;
  let frozenColumnsWidth = 0;
  for (let i = 0; i < columnCount; i++) {
    const visibleCellIndex = dataTable.column(i).index('visible');
    if (visibleCellIndex !== null) { // Ignore invisible columns
      frozenColumnsWidth += allTableColumnHeaderCells[visibleCellIndex].offsetWidth;
    }
  }

  return table.offsetWidth >= frozenColumnsWidth + minScrollableSpace;
}

// calculates minimum space that needs to fit on the right side of the table after applying frozen columns (the scrollable part)
function calculateMinScrollableSpace (
  tableColumnsCount: number,
  columnCount: number,
  allTableColumns: HTMLElement[],
  table: ResultTable) {
  // when not all columns are frozen get the width of the first not frozen
  if (tableColumnsCount > columnCount) {
    const visibleCellIndex = table.getDataTable()!.column(columnCount - 1)
      .index('visible') + 1;
    return allTableColumns[visibleCellIndex].offsetWidth;
  } else {
    // get the default width
    return DEFAULT_MIN_SPACE_LEFT;
  }
}

function findVisibleColumnCountUpUntilTheGivenIndex (columnIndexExclusive: number, columns: TableColumnDto[]) {
  let count = 0;
  for (let i = 0; i < columnIndexExclusive; i++) {
    if (columns[i].visible) {
      count++;
    }
  }
  return count;
}

customElements.define(COMPONENT_NAME, FrozenColumnsOlapPivotTablePlugin);
